import { CloseOutlined, DownOutlined } from "@ant-design/icons"
import {
  Alert,
  App,
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Form,
  Result,
  Select,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography
} from "antd"
import type { TextAreaRef } from "antd/es/input/TextArea"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../components/Input/TextInput.tsx"
import {
  RichTextEditor,
  type RichTextEditorHandle
} from "../../../components/RichTextEditor/RichTextEditor.tsx"
import { RichTextViewer } from "../../../components/RichTextEditor/RichTextViewer.tsx"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import {
  type ContactSuggestion,
  type RecordFile,
  getRecordFiles,
  getSendState,
  previewMessage,
  searchContacts,
  sendMessage
} from "../api/compose.ts"
import {
  getCompanyTemplates,
  getPersonalTemplates,
  getPlaceholders
} from "../api/templates.ts"
import { formatUsPhone } from "../settings/format-phone.ts"
import { MAX_SEGMENTS, countSegments } from "../settings/sms-segments.ts"
import type { PlaceholderGroup } from "../types/communication.types.ts"
import type {
  Channel,
  ComposeOutcome,
  ContactRef,
  PreviewResult,
  RecordRef,
  SendState
} from "../types/compose.types.ts"

export interface ComposerTarget {
  record: RecordRef
  recipient: ContactRef
  recipientName: string
}

interface TemplateOption {
  id: string
  name: string
  channel: Channel
  category: "transactional" | "marketing"
  subject: string
  body: string
  personal: boolean
}

interface DraftForm {
  template?: string
  subject: string
  body: string
  cc: string[]
  bcc: string[]
  saveAsTemplate: boolean
  templateName: string
  attachments: string[]
}

const EMPTY: DraftForm = {
  attachments: [],
  subject: "",
  body: "",
  cc: [],
  bcc: [],
  saveAsTemplate: false,
  templateName: ""
}

interface ErrorBody {
  details?: Record<string, unknown>
}

const errorEntries = (data: unknown) => {
  const details = (data as ErrorBody | null)?.details ?? {}
  const map: Record<string, keyof DraftForm> = {
    subject: "subject",
    body: "body",
    cc: "cc",
    bcc: "bcc",
    save_as_template_name: "templateName"
  }
  return Object.entries(map)
    .filter(([key]) => details[key] !== undefined)
    .map(([key, name]) => ({
      name,
      errors: (Array.isArray(details[key])
        ? (details[key] as unknown[])
        : [details[key]]
      ).map(String)
    }))
}

const resultsOf = <T,>(data: { results: T[] } | T[] | undefined) =>
  Array.isArray(data) ? data : (data?.results ?? [])

const OutcomeList = ({
  outcome,
  onDone
}: {
  outcome: ComposeOutcome
  onDone: () => void
}) => {
  const { t } = useTranslation()
  const reason = (code: string) =>
    code
      ? t(`communication.composer.reasons.${code}`, { defaultValue: code })
      : ""
  return (
    <Result
      status={
        outcome.skipped.length && !outcome.sent.length ? "warning" : "success"
      }
      title={t("communication.composer.result.title", {
        sent: outcome.sent.length,
        skipped: outcome.skipped.length
      })}
      extra={
        <Button onClick={onDone}>
          {t("communication.composer.result.done")}
        </Button>
      }
    >
      <ul
        aria-label={t("communication.composer.result.list")}
        className="flex flex-col gap-2 text-start"
      >
        {outcome.sent.map((row) => (
          <li key={row.message_id}>
            <Typography.Text strong>{row.to_address}</Typography.Text>{" "}
            {row.reason
              ? t("communication.composer.result.held", {
                  reason: reason(row.reason)
                })
              : t("communication.composer.result.sent")}
          </li>
        ))}
        {outcome.skipped.map((row) => (
          <li key={row.message_id}>
            <Typography.Text strong>
              {row.to_address || t("communication.composer.result.noAddress")}
            </Typography.Text>{" "}
            <Typography.Text type="danger">
              {t("communication.composer.result.skipped", {
                reason: reason(row.reason)
              })}
            </Typography.Text>
          </li>
        ))}
      </ul>
    </Result>
  )
}

const ComposerDrawer = ({
  target,
  onClose
}: {
  target: ComposerTarget | null
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { formatDate, dateFormat } = useDateFormatter()
  const [form] = Form.useForm<DraftForm>()
  const [channel, setChannel] = useState<Channel>("email")
  const [sendState, setSendState] = useState<SendState | null>(null)
  const [stateFailed, setStateFailed] = useState(false)
  const [templates, setTemplates] = useState<TemplateOption[]>([])
  const [groups, setGroups] = useState<PlaceholderGroup[]>([])
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [sending, setSending] = useState(false)
  const [outcome, setOutcome] = useState<ComposeOutcome | null>(null)
  const [recordFiles, setRecordFiles] = useState<RecordFile[]>([])
  const [extraRecipients, setExtraRecipients] = useState<ContactSuggestion[]>(
    []
  )
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([])
  const searchSeq = useRef(0)
  const editorRef = useRef<RichTextEditorHandle>(null)
  const smsRef = useRef<TextAreaRef>(null)

  const body: string = Form.useWatch("body", form) ?? ""
  const saveAsTemplate: boolean = Form.useWatch("saveAsTemplate", form) ?? false
  const templateId: string | undefined = Form.useWatch("template", form)

  useEffect(() => {
    if (!target) return
    setChannel("email")
    setSendState(null)
    setStateFailed(false)
    setPreview(null)
    setOutcome(null)
    form.setFieldsValue({ ...EMPTY, template: undefined })
    setRecordFiles([])
    setExtraRecipients([])
    setSuggestions([])
    if (target.record.type === "lead" || target.record.type === "project") {
      void getRecordFiles(target.record.id).then(setRecordFiles)
    }
    void getSendState(target.recipient).then((response) => {
      if (response.ok && response.data) setSendState(response.data)
      else setStateFailed(true)
    })
    void Promise.all([
      getCompanyTemplates(),
      getPersonalTemplates(),
      getPlaceholders()
    ]).then(([company, personal, placeholders]) => {
      setTemplates([
        ...(company.ok
          ? resultsOf(company.data).map((item) => ({
              ...item,
              personal: false
            }))
          : []),
        ...(personal.ok
          ? resultsOf(personal.data).map((item) => ({
              ...item,
              channel: "email" as const,
              category: "transactional" as const,
              personal: true
            }))
          : [])
      ])
      if (placeholders.ok && placeholders.data)
        setGroups(placeholders.data.groups)
    })
  }, [target, form])

  const chosenTemplate = useMemo(
    () => templates.find((item) => item.id === templateId),
    [templates, templateId]
  )
  const category = chosenTemplate?.category ?? "transactional"
  const state = sendState?.channels[channel]
  const smsState = sendState?.channels.sms
  const smsBlocked =
    !!smsState && (smsState.state === "stopped" || !smsState.available)
  const categoryRefusal =
    state && !state.can_send[category].allowed
      ? state.can_send[category].reason
      : null

  const switchChannel = (next: Channel) => {
    setChannel(next)
    setPreview(null)
    setExtraRecipients([])
    setSuggestions([])
    form.setFieldsValue({ ...EMPTY, template: undefined })
  }

  const findRecipients = (text: string) => {
    const seq = ++searchSeq.current
    if (text.trim().length < 2) {
      setSuggestions([])
      return
    }
    void searchContacts(text.trim(), channel).then((response) => {
      if (seq !== searchSeq.current) return
      setSuggestions(response.ok ? (response.data?.results ?? []) : [])
    })
  }

  const isChosen = (contact: ContactRef) =>
    (contact.type === target?.recipient.type &&
      contact.id === target?.recipient.id) ||
    extraRecipients.some(
      (extra) => extra.type === contact.type && extra.id === contact.id
    )

  const addRecipient = (key: string) => {
    const picked = suggestions.find(
      (contact) => `${contact.type}:${contact.id}` === key
    )
    if (picked && !isChosen(picked))
      setExtraRecipients((current) => [...current, picked])
    setSuggestions([])
  }

  const applyTemplate = (id?: string) => {
    const picked = templates.find((item) => item.id === id)
    if (!picked) return
    form.setFieldsValue({ subject: picked.subject, body: picked.body })
  }

  const insertField = (token: string) => {
    if (channel === "email") {
      editorRef.current?.insertText(token)
      return
    }
    const element = smsRef.current?.resizableTextArea?.textArea
    const current: string = form.getFieldValue("body") ?? ""
    const start = element?.selectionStart ?? current.length
    const end = element?.selectionEnd ?? current.length
    form.setFieldValue(
      "body",
      current.slice(0, start) + token + current.slice(end)
    )
  }

  const draft = () => {
    const values = form.getFieldsValue(true) as DraftForm
    return {
      channel,
      record: target!.record,
      ...(values.template ? { template: values.template } : {}),
      subject: channel === "email" ? values.subject : "",
      body: values.body
    }
  }

  const showPreview = async () => {
    const response = await previewMessage({
      ...draft(),
      recipient: target!.recipient
    })
    if (!response.ok || !response.data) {
      form.setFields(errorEntries(response.data))
      message.error(t("communication.composer.previewFailed"))
      return
    }
    setPreview(unwrapEnvelope<PreviewResult>(response.data))
  }

  const send = async (values: DraftForm) => {
    setSending(true)
    const name = values.saveAsTemplate ? values.templateName.trim() : ""
    const response = await sendMessage({
      ...draft(),
      recipients: [
        target!.recipient,
        ...extraRecipients.map(({ type, id }) => ({ type, id }))
      ],
      ...(channel === "email" ? { cc: values.cc, bcc: values.bcc } : {}),
      ...(channel === "email" && values.attachments?.length
        ? {
            attachments: values.attachments.map((ref) => {
              const [type, id] = ref.split(":")
              return { type: type as "document" | "photo", id }
            })
          }
        : {}),
      ...(name ? { save_as_template_name: name } : {})
    })
    setSending(false)
    if (!response.ok) {
      const errors = errorEntries(response.data)
      form.setFields(errors)
      if (!errors.length) message.error(t("communication.composer.sendFailed"))
      return
    }
    setOutcome(unwrapEnvelope<ComposeOutcome>(response.data))
  }

  const fieldMenu = {
    items: groups.map((group) => ({
      type: "group" as const,
      key: group.group,
      label: t(`communication.templates.groups.${group.group}`, {
        defaultValue: group.group
      }),
      children: group.placeholders.map((field) => ({
        key: field.token,
        label: field.label
      }))
    })),
    onClick: ({ key }: { key: string }) => insertField(key)
  }

  const templateOptions = templates
    .filter((item) => item.channel === channel)
    .map((item) => ({
      value: item.id,
      label: item.personal
        ? t("communication.composer.personalTemplate", { name: item.name })
        : item.name
    }))

  const smsBlockedNotice = smsState && smsBlocked && (
    <Alert
      type="warning"
      showIcon
      message={
        smsState.state === "stopped"
          ? t("communication.composer.sms.optedOut", {
              date: smsState.state_changed_at
                ? formatDate(smsState.state_changed_at, dateFormat)
                : ""
            })
          : t("communication.composer.sms.notReady")
      }
      action={
        smsState.state === "stopped" ? (
          <Button size="small" onClick={() => switchChannel("email")}>
            {t("communication.composer.sms.useEmail")}
          </Button>
        ) : undefined
      }
    />
  )

  const composeBody = (
    <Form<DraftForm>
      form={form}
      layout="vertical"
      initialValues={EMPTY}
      onFinish={send}
    >
      {state?.sender?.address && (
        <p className="mb-1">
          <Typography.Text type="secondary">
            {t("communication.composer.from")}:{" "}
          </Typography.Text>
          <Typography.Text>
            {channel === "sms"
              ? formatUsPhone(state.sender.address)
              : state.sender.name
                ? `${state.sender.name} <${state.sender.address}>`
                : state.sender.address}
          </Typography.Text>
        </p>
      )}
      <p className="mb-3">
        <Typography.Text type="secondary">
          {t("communication.composer.to")}:{" "}
        </Typography.Text>
        <Typography.Text strong>{target?.recipientName}</Typography.Text>{" "}
        {state?.address ? (
          <Typography.Text type="secondary">
            {channel === "sms" ? formatUsPhone(state.address) : state.address}
          </Typography.Text>
        ) : (
          sendState && (
            <Typography.Text type="danger">
              {t(`communication.composer.noAddress.${channel}`)}
            </Typography.Text>
          )
        )}
      </p>
      {extraRecipients.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {extraRecipients.map((extra) => (
            <Tag
              key={`${extra.type}:${extra.id}`}
              closable
              closeIcon={
                <CloseOutlined
                  aria-label={t("communication.composer.removeRecipient", {
                    name: extra.name
                  })}
                />
              }
              onClose={() =>
                setExtraRecipients((current) =>
                  current.filter((item) => item !== extra)
                )
              }
            >
              {extra.name}{" "}
              <Typography.Text type="secondary">
                {channel === "sms"
                  ? formatUsPhone(extra.address)
                  : extra.address}
              </Typography.Text>
            </Tag>
          ))}
        </div>
      )}
      <Select
        className="mb-3 w-full"
        showSearch
        value={null as string | null}
        filterOption={false}
        aria-label={t("communication.composer.addRecipient")}
        placeholder={t("communication.composer.addRecipient")}
        notFoundContent={null}
        onSearch={findRecipients}
        onSelect={addRecipient}
        options={suggestions
          .filter((contact) => !isChosen(contact))
          .map((contact) => ({
            value: `${contact.type}:${contact.id}`,
            label: `${contact.name} · ${
              channel === "sms"
                ? formatUsPhone(contact.address)
                : contact.address
            }`
          }))}
      />
      {categoryRefusal && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t(`communication.composer.reasons.${categoryRefusal}`, {
            defaultValue: categoryRefusal
          })}
        />
      )}
      <Form.Item name="template" label={t("communication.composer.template")}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          options={templateOptions}
          placeholder={t("communication.composer.templatePlaceholder")}
          onChange={applyTemplate}
        />
      </Form.Item>
      {channel === "email" && (
        <>
          <div className="flex flex-col gap-x-4 sm:flex-row">
            <Form.Item
              name="cc"
              label={t("communication.composer.cc")}
              className="sm:flex-1"
            >
              <Select
                mode="tags"
                open={false}
                suffixIcon={null}
                tokenSeparators={[",", " "]}
              />
            </Form.Item>
            <Form.Item
              name="bcc"
              label={t("communication.composer.bcc")}
              className="sm:flex-1"
            >
              <Select
                mode="tags"
                open={false}
                suffixIcon={null}
                tokenSeparators={[",", " "]}
              />
            </Form.Item>
          </div>
          <Form.Item
            name="subject"
            label={t("communication.composer.subject")}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("communication.composer.subjectRequired")
              }
            ]}
          >
            <TextInput maxLength={998} />
          </Form.Item>
        </>
      )}
      <div className="mb-2 flex items-center justify-between">
        <Typography.Text>{t("communication.composer.message")}</Typography.Text>
        <Dropdown
          menu={fieldMenu}
          trigger={["click"]}
          disabled={!groups.length}
        >
          <Button size="small">
            {t("communication.templates.editor.insertField")}{" "}
            <DownOutlined aria-hidden />
          </Button>
        </Dropdown>
      </div>
      {channel === "email" ? (
        <Form.Item
          name="body"
          rules={[
            {
              required: true,
              message: t("communication.composer.messageRequired")
            }
          ]}
        >
          <RichTextEditor
            ref={editorRef}
            value=""
            onChange={() => undefined}
            ariaLabel={t("communication.composer.message")}
            minHeight={200}
            variant="compact"
          />
        </Form.Item>
      ) : (
        <>
          <Form.Item
            name="body"
            className="mb-1"
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("communication.composer.messageRequired")
              }
            ]}
          >
            <TextArea
              ref={smsRef}
              rows={5}
              aria-label={t("communication.composer.message")}
              disableAutoCapitalize
            />
          </Form.Item>
          <Typography.Text
            type={
              countSegments(body).segments > MAX_SEGMENTS
                ? "danger"
                : "secondary"
            }
            className="text-xs"
            aria-live="polite"
          >
            {t("communication.templates.editor.segments", {
              characters: countSegments(body).characters,
              count: countSegments(body).segments,
              encoding: countSegments(body).encoding
            })}
          </Typography.Text>
        </>
      )}
      {preview && (
        <section
          aria-label={t("communication.composer.previewTitle", {
            name: target?.recipientName
          })}
          className="mt-4 rounded-lg border border-solid border-gray-200 p-4"
        >
          <Typography.Text type="secondary" className="mb-2 block text-xs">
            {t("communication.composer.previewTitle", {
              name: target?.recipientName
            })}
          </Typography.Text>
          {preview.channel === "email" ? (
            <>
              <p className="mb-2 font-semibold">{preview.subject}</p>
              <RichTextViewer html={preview.body} allowImages />
            </>
          ) : (
            <>
              <p className="whitespace-pre-wrap">{preview.text}</p>
              {preview.sms && (
                <Typography.Text type="secondary" className="text-xs">
                  {t("communication.templates.editor.segments", {
                    characters: preview.sms.characters,
                    count: preview.sms.segments,
                    encoding: preview.sms.encoding
                  })}
                </Typography.Text>
              )}
            </>
          )}
        </section>
      )}
      {channel === "email" && recordFiles.length > 0 && (
        <Form.Item
          name="attachments"
          label={t("communication.composer.attachments")}
          className="mt-4"
        >
          <Select
            mode="multiple"
            allowClear
            optionFilterProp="label"
            aria-label={t("communication.composer.attachments")}
            placeholder={t("communication.composer.attachmentsPlaceholder")}
            options={recordFiles.map((file) => ({
              value: file.ref,
              label: file.name
            }))}
          />
        </Form.Item>
      )}
      {channel === "email" && (
        <div className="mt-4">
          <Form.Item
            name="saveAsTemplate"
            valuePropName="checked"
            className="mb-2"
          >
            <Checkbox>{t("communication.composer.saveAsTemplate")}</Checkbox>
          </Form.Item>
          {saveAsTemplate && (
            <Form.Item
              name="templateName"
              label={t("communication.templates.editor.name")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("communication.templates.editor.nameRequired")
                }
              ]}
            >
              <TextInput maxLength={255} />
            </Form.Item>
          )}
        </div>
      )}
    </Form>
  )

  const tabBody = (tab: Channel) =>
    tab !== channel
      ? null
      : tab === "sms" && smsBlocked
        ? smsBlockedNotice
        : composeBody

  return (
    <Drawer
      open={!!target}
      onClose={onClose}
      width={760}
      destroyOnHidden
      title={t("communication.composer.title", { name: target?.recipientName })}
      extra={
        !outcome && (
          <Space>
            <Button
              onClick={showPreview}
              disabled={channel === "sms" && smsBlocked}
            >
              {t("communication.composer.previewAs", {
                name: target?.recipientName
              })}
            </Button>
            <Button
              type="primary"
              loading={sending}
              disabled={!state?.address || (channel === "sms" && smsBlocked)}
              onClick={() => form.submit()}
            >
              {t("communication.composer.send")}
            </Button>
          </Space>
        )
      }
    >
      {outcome ? (
        <OutcomeList outcome={outcome} onDone={onClose} />
      ) : stateFailed ? (
        <Alert
          type="error"
          showIcon
          message={t("communication.composer.stateFailed")}
        />
      ) : !sendState ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Tabs
          activeKey={channel}
          onChange={(key) => switchChannel(key as Channel)}
          items={(["email", "sms"] as const).map((tab) => ({
            key: tab,
            label: t(`communication.templates.channel.${tab}`),
            children: tabBody(tab)
          }))}
        />
      )}
    </Drawer>
  )
}

export default ComposerDrawer
